import React from 'react';
import { Icon, IconSize, IconType } from '../Icon';
import styled from 'styled-components';
import { IHasTestData } from '../../test-utilities/IHasTestData';

export type TCheckboxValue = string | number | string[] | undefined;

export type TOnChange = (name: TCheckboxValue, checked: boolean) => void;

type TValidHeights = 20 | 24 | 28 | 32;

const StyledLabel = styled.label`
  align-items: center;
  cursor: pointer;
  display: inline-grid;
  gap: ${(props) => props.theme.spacing.spacing2};
  grid-auto-flow: column;
  grid-auto-columns: max-content;
`;

const StyledToggleInput = styled.input<{ height: TValidHeights }>`
  appearance: none;
  cursor: pointer;
  display: block;
  background-color: ${(props) => (props.checked ? props.theme.colors.primary : props.theme.colors.gray200)};

  :focus {
    background-color: ${(props) => (props.checked ? props.theme.colors.primary : props.theme.colors.gray400)};
    box-shadow:
      0px 0px 0px 2px ${(props) => props.theme.colors.defaultContentBackground},
      0px 0px 0px 4px ${(props) => props.theme.colors.primary};
  }

  border-radius: ${(props) => props.height / 2}px;
  height: ${(props) => props.height}px;
  margin: 0;
  position: relative;
  width: ${(props) => props.height * 2}px;
`;

const StyledToggleIcon = styled.span<{ checked: boolean; height: TValidHeights }>`
  align-items: center;
  background-color: ${(props) => props.theme.colors.white};
  border: 1px solid ${(props) => props.theme.colors.white};
  border-radius: ${(props) => (props.height - 4) / 2}px;
  content: '';
  display: block;
  box-shadow:
    0px 1px 3px ${(props) => props.theme.colors.shadowVariant1},
    0px 1px 2px ${(props) => props.theme.colors.shadowVariant2};
  height: ${(props) => props.height - 4}px;
  left: ${(props) => (props.checked ? props.height + 2 : 2)}px;
  top: 2px;
  transition-property: left;
  transition-duration: 0.3s;
  position: absolute;
  width: ${(props) => props.height - 4}px;

  > svg:last-child {
    display: none;
  }
`;
const StyledTogglePill = styled.span<{ checked: boolean; height: TValidHeights; disabled: boolean | undefined }>`
  display: block;
  position: relative;
  &:hover {
    ${StyledToggleInput}:not(:focus) {
      background-color: ${(props) =>
        props.checked ? props.theme.colors.primary : props.theme.colors.secondarySelectedBackground};
      cursor: ${(props) => props.disabled && 'not-allowed'};
    }
  }
`;

export interface IToggleSwitchProps extends IHasTestData {
  /** Additional wrapper CSS class. */
  className?: string;
  /** Component checked state. */
  checked: boolean;
  /** Switches the input to disabled which prevents editing the contents. */
  disabled?: boolean;
  /** HTML Id attribute. */
  id: string;
  /** This label appears above the input. */
  label?: string;
  /** A callback triggered when focus is removed. */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** A callback fired when the value prop changes. */
  onChange: TOnChange;
  /** HTML value attribute. */
  value: TCheckboxValue;
  /** Height of the Control itself v2 only */
  height?: TValidHeights;
}

/**
 * The HTML input (checkbox type) with a label.
 */
export const ToggleSwitch: React.FC<IToggleSwitchProps> = ({ height = 20, ...props }) => (
  <StyledLabel htmlFor={props.id} data-test-class={props['data-test-class'] || 'toggle-label'}>
    <StyledTogglePill checked={props.checked} height={height} disabled={props.disabled}>
      <StyledToggleInput
        checked={props.checked}
        id={props.id}
        onBlur={props.onBlur}
        onChange={props.disabled ? undefined : (event) => props.onChange(props.value, event.target.checked)}
        type="checkbox"
        value={props.value}
        height={height}
        data-test-class={props['data-test-class'] || 'toggle-input'}
        data-test-id={props['data-test-id']}
      />
      <StyledToggleIcon checked={props.checked} height={height}>
        <Icon type={props.checked ? IconType.CloseCircleFill : IconType.CloseCircleLine} size={IconSize.Fit} />
      </StyledToggleIcon>
    </StyledTogglePill>
    {props.label && <span>{props.label}</span>}
  </StyledLabel>
);
